/**
 * RackElementQuickCreate — alta rápida de **switch / patch panel / PDU** desde el
 * propio visor de rack.
 *
 * Por qué existe
 * --------------
 * Antes, para montar un switch en un rack había que salir del visor, ir al
 * listado, pulsar «Nuevo», rellenar la ficha entera (fabricante, modelo, serie,
 * descripción…) y volver a asignar rack y ubicación. Aquí solo se piden los tres
 * datos que hacen falta para montarlo:
 *
 *   · **tipo**    — lo decide el botón con el que se abre el diálogo,
 *   · **nombre**  — prerrellenado con el **siguiente número de la nomenclatura**
 *                   (`/api/naming/suggest`, que es un preview y NO consume),
 *   · **puertos** — `port_count` para switch/panel y `num_outlets` para PDU.
 *
 * El **rack y la ubicación vienen ya puestos** del visor (el backend, además,
 * hereda la ubicación del rack), y la posición se elige en el propio diálogo
 * entre las U que están libres de verdad — se reciben ya calculadas desde
 * `RackViewer` (`occupied.front` / `occupied.rear` incluyen elementos, tapas y
 * el bloqueo de profundidad completa), así que no se puede pedir una U ocupada
 * y comerse un 400 del backend.
 *
 * Desde el «modo tablet» se abre tocando un hueco libre: `initialU` (la U
 * SUPERIOR tocada, ver `utils/rackPlacement.js`) e `initialSide` (la cara que
 * estabas viendo) llegan como props y el diálogo nace ya colocado ahí.
 *
 * El número definitivo de la nomenclatura lo consume el backend al crear
 * (`consume_naming_on_create`), no este diálogo.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import Dialog from './common/Dialog';

const TYPE_META = {
  server: {
    icon: '🖥️',
    color: '#2563eb',
    defaultPorts: null,
    defaultSize: 2,
    defaultSide: 'front',
    portsIsOutlets: false,
    hasPorts: false,
  },
  switch: {
    icon: '🔀',
    color: '#28a745',
    defaultPorts: 24,
    defaultSide: 'front',
    portsIsOutlets: false,
  },
  panel: {
    icon: '🔗',
    color: '#17a2b8',
    defaultPorts: 24,
    defaultSide: 'front',
    portsIsOutlets: false,
  },
  pdu: {
    icon: '⚡',
    color: '#ffc107',
    defaultPorts: 8,
    defaultSide: 'rear',
    portsIsOutlets: true,
  },
};

const PORT_PRESETS = [8, 12, 16, 24, 48];
const SIZE_PRESETS = [1, 2];
const SERVER_SIZE_PRESETS = [1, 2, 4];

/** Primer arranque de U (de abajo arriba) donde cabe `size` sin pisar `occ`. */
function firstFreeStart(occ, size, heightU) {
  for (let u = heightU - size + 1; u >= 1; u -= 1) {
    let free = true;
    for (let k = 0; k < size; k += 1) {
      if (occ.has(u + k)) { free = false; break; }
    }
    if (free) return u;
  }
  return null;
}

/** ¿Cabe un elemento de `size` U empezando en `start` (U inferior)? */
function fitsAt(occ, start, size, heightU) {
  if (!start || start < 1 || start + size - 1 > heightU) return false;
  for (let k = 0; k < size; k += 1) {
    if (occ.has(start + k)) return false;
  }
  return true;
}

export default function RackElementQuickCreate({
  elementType,
  companyId,
  rackId,
  locationId,
  rackName,
  locationName,
  heightU = 42,
  occupied = { front: new Set(), rear: new Set() },
  // Hueco tocado en el visor (modo tablet): U SUPERIOR y cara.
  initialU = null,
  initialSide = null,
  designMode = false,
  onClose,
  onCreated,
}) {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  const meta = TYPE_META[elementType] || TYPE_META.switch;
  const hasPorts = meta.hasPorts !== false;
  const sizePresets = hasPorts ? SIZE_PRESETS : SERVER_SIZE_PRESETS;
  const defaultSize = meta.defaultSize || 1;

  const [form, setForm] = useState(() => {
    // La cara tocada manda sobre la de por defecto del tipo (una PDU nace en la
    // trasera, pero si estás mirando el frontal y tocas un hueco, va ahí).
    const side = initialSide === 'rear' || initialSide === 'front' ? initialSide : meta.defaultSide;
    const initialOcc = occupied?.[side] || new Set();
    // `initialU` es la U SUPERIOR del hueco tocado; `position_u` guarda la
    // INFERIOR, así que hay que descontar la altura del equipo.
    const wantedTop = Number(initialU) || null;
    const wantedStart = wantedTop ? wantedTop - defaultSize + 1 : null;
    const start = (wantedStart && fitsAt(initialOcc, wantedStart, defaultSize, heightU))
      ? wantedStart
      : firstFreeStart(initialOcc, defaultSize, heightU);
    return {
      name: '',
      ports: meta.defaultPorts,
      sizeU: defaultSize,
      side,
      positionU: start ? String(start) : '',
    };
  });
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  /** Rellena el nombre con el siguiente de la nomenclatura (preview, no consume). */
  const loadSuggestion = async ({ silent } = {}) => {
    if (!companyId) return;
    if (!silent) setSuggesting(true);
    try {
      const resp = await api.suggestName({
        type: elementType,
        companyId,
        rackId: rackId || undefined,
        locationId: locationId || undefined,
        // El ✨ explícito reserva el número; la propuesta al abrir es solo un
        // preview para no gastar numeración si se cancela el diálogo.
        consume: !silent,
      });
      const suggested = resp?.data?.suggested_name;
      if (suggested) setForm((prev) => ({ ...prev, name: suggested }));
    } catch (_) {
      if (!silent) setError(tr('rack.add_suggest_error', 'No se pudo generar el nombre. Escríbelo a mano.'));
    } finally {
      if (!silent) setSuggesting(false);
    }
  };

  // Nombre propuesto al abrir: el siguiente de la nomenclatura para este rack.
  // En modo diseno NO se sugiere: el borrador no consume numeracion y el nombre
  // tiene que ser el hostname real (es la llave con la que el agente lo adopta).
  useEffect(() => {
    if (!designMode) loadSuggestion({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** U libres donde cabe el tamaño actual, de arriba abajo (convención del rack). */
  const freeStarts = useMemo(() => {
    const occ = occupied?.[form.side] || new Set();
    const size = Number(form.sizeU) || 1;
    const out = [];
    for (let u = heightU - size + 1; u >= 1; u -= 1) {
      if (fitsAt(occ, u, size, heightU)) out.push(u);
    }
    return out;
  }, [occupied, form.side, form.sizeU, heightU]);

  /**
   * Cambiar cara o altura puede invalidar la U elegida: se recoloca en el
   * primer hueco libre de la nueva cara (o «sin ubicar» si no queda ninguno).
   * «Sin ubicar» ('') se respeta siempre.
   */
  const changePlacement = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      const size = Number(next.sizeU) || 1;
      const occ = occupied?.[next.side] || new Set();
      const current = next.positionU === '' ? null : Number(next.positionU);
      const keep = current ? fitsAt(occ, current, size, heightU) : next.positionU === '';
      if (keep) return next;
      const fallback = firstFreeStart(occ, size, heightU);
      return { ...next, positionU: fallback ? String(fallback) : '' };
    });
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (saving) return;
    const name = form.name.trim();
    if (!name) {
      setError(tr('rack.add_name_required', 'El nombre es obligatorio.'));
      return;
    }
    const ports = Number(form.ports);
    if (hasPorts && (!ports || ports < 1)) {
      setError(tr('rack.add_ports_required', 'Indica un número de puertos mayor que 0.'));
      return;
    }
    setError('');
    setSaving(true);
    try {
      const payload = {
        element_type: elementType,
        name,
        // El código arranca igual que el nombre: en un patch panel suelen ser
        // el mismo texto y así el listado no enseña la columna vacía.
        code: name,
        rack_id: Number(rackId),
        location_id: locationId ? Number(locationId) : undefined,
        size_u: Number(form.sizeU) || 1,
        position_u: form.positionU === '' ? null : Number(form.positionU),
        position_front: form.side === 'front',
        status: 'active',
      };
      if (hasPorts) {
        if (meta.portsIsOutlets) payload.num_outlets = ports;
        else payload.port_count = ports;
      }
      // En un rack en borrador el elemento nace "planificado": no cuenta como
      // inventario y no se ofrece en los listados de produccion.
      if (designMode) payload.install_state = 'planned';

      const resp = await api.createElement(payload, companyId);
      const created = resp?.data || resp;
      onCreated?.(created?.id ?? null, created);
    } catch (err) {
      setError(err.message || tr('rack.add_error', 'No se pudo crear el elemento.'));
    } finally {
      setSaving(false);
    }
  };

  const titleByType = {
    server: designMode
      ? tr('rack.add_title_design_server', 'Nuevo servidor de diseño')
      : tr('rack.add_title_server', 'Nuevo servidor'),
    switch: tr('rack.add_title_switch', 'Nuevo switch'),
    panel: tr('rack.add_title_panel', 'Nuevo patch panel'),
    pdu: tr('rack.add_title_pdu', 'Nueva PDU'),
  };
  const portsLabel = meta.portsIsOutlets
    ? tr('rack.add_outlets', 'Nº de enchufes')
    : tr('rack.add_ports', 'Nº de puertos');

  const fieldLabel = {
    display: 'block',
    marginBottom: 6,
    fontSize: '0.78rem',
    fontWeight: 600,
    color: 'var(--text-secondary)',
  };

  return (
    <Dialog
      title={titleByType[elementType] || tr('rack.add_element', 'Añadir equipo')}
      icon={meta.icon}
      onClose={onClose}
      busy={saving}
      className="rqc-dialog"
      maxWidth={470}
      footer={(
        <>
          <button type="button" className="list-btn-secondary" onClick={onClose} disabled={saving}>
            {tr('common.cancel', 'Cancelar')}
          </button>
          <button type="button" className="list-btn-primary" onClick={() => handleSubmit()} disabled={saving}>
            {saving ? tr('rack.add_creating', 'Creando…') : tr('rack.add_create', 'Crear')}
          </button>
        </>
      )}
    >
      <form id="rqc-form" onSubmit={handleSubmit}>
        {/* Dónde nace: se hereda del visor, no se pregunta. */}
        <div className="rqc-context">
          <span className="rqc-chip">🗄️ {rackName || `#${rackId}`}</span>
          {(locationName || locationId) && (
            <span className="rqc-chip">📍 {locationName || `#${locationId}`}</span>
          )}
        </div>

        <div style={{ marginBottom: '0.85rem' }}>
          <label htmlFor="rqc-name" style={fieldLabel}>
            {tr('rack.add_name', 'Nombre')}
          </label>
          <div className="rqc-name-row">
            <input
              id="rqc-name"
              type="text"
              className="list-input"
              style={{ width: '100%', minWidth: 0 }}
              value={form.name}
              autoFocus
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder={designMode
                ? tr('rack.add_name_design_placeholder', 'Hostname real del equipo (p. ej. SRV-CORE-01)')
                : tr('rack.add_name_placeholder', 'Siguiente de la nomenclatura…')}
            />
            {!designMode && (
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => loadSuggestion()}
                disabled={suggesting || saving}
                title={tr('rack.add_suggest', 'Sugerir nombre')}
              >
                {suggesting ? '…' : '✨'}
              </button>
            )}
          </div>
          {designMode && (
            <p className="rqc-warn" style={{ marginTop: 6 }}>
              {tr(
                'rack.add_design_hint',
                'Usa el nombre con el que el equipo se dará de alta: el agente lo empareja por hostname y rellena serie, IP y MAC.',
              )}
            </p>
          )}
        </div>

        {hasPorts && (
        <div style={{ marginBottom: '0.85rem' }}>
          <label htmlFor="rqc-ports" style={fieldLabel}>{portsLabel}</label>
          <div className="rqc-ports-row">
            {PORT_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={`rack-filler-btn${Number(form.ports) === preset ? ' rqc-preset--active' : ''}`}
                onClick={() => setForm((prev) => ({ ...prev, ports: preset }))}
              >
                {preset}
              </button>
            ))}
            <input
              id="rqc-ports"
              type="number"
              min="1"
              className="list-input rqc-ports-input"
              value={form.ports}
              onChange={(e) => setForm((prev) => ({ ...prev, ports: e.target.value }))}
            />
          </div>
        </div>
        )}

        <div className="rqc-grid">
          <div>
            <label htmlFor="rqc-side" style={fieldLabel}>{tr('rack.add_side', 'Cara')}</label>
            <select
              id="rqc-side"
              className="list-select"
              value={form.side}
              onChange={(e) => changePlacement({ side: e.target.value })}
            >
              <option value="front">{tr('rack.side_front', 'Frontal')}</option>
              <option value="rear">{tr('rack.side_rear', 'Trasera')}</option>
            </select>
          </div>
          <div>
            <label htmlFor="rqc-size" style={fieldLabel}>{tr('rack.add_size', 'Altura (U)')}</label>
            <select
              id="rqc-size"
              className="list-select"
              value={form.sizeU}
              onChange={(e) => changePlacement({ sizeU: Number(e.target.value) })}
            >
              {sizePresets.map((size) => (
                <option key={size} value={size}>{size}U</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="rqc-position" style={fieldLabel}>{tr('rack.add_position', 'Posición')}</label>
            <select
              id="rqc-position"
              className="list-select"
              value={form.positionU}
              onChange={(e) => setForm((prev) => ({ ...prev, positionU: e.target.value }))}
            >
              <option value="">{tr('rack.add_unplaced', 'Sin ubicar')}</option>
              {freeStarts.map((u) => {
                const size = Number(form.sizeU) || 1;
                const top = u + size - 1;
                const label = size === 1 ? `U${u}` : `U${u} → U${top}`;
                return <option key={u} value={String(u)}>{label}</option>;
              })}
            </select>
          </div>
        </div>

        {freeStarts.length === 0 && (
          <p className="rqc-warn">
            {tr('rack.add_no_free', 'No queda hueco libre en esta cara con esa altura. Se creará «sin ubicar».')}
          </p>
        )}

        {error && <p className="rqc-error">{error}</p>}
      </form>
    </Dialog>
  );
}
